import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import type { ThemeContextValue, ThemeId, ThemeConfig } from "./theme";
import {
  THEME_STORAGE_KEY,
  themes,
  systemDarkDefault,
  systemLightDefault,
} from "./theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolveTheme(themeId: ThemeId): ThemeConfig {
  if (themeId === "system") {
    return getSystemPrefersDark() ? systemDarkDefault : systemLightDefault;
  }
  return themes[themeId] ?? themes.paper;
}

function applyTheme(theme: ThemeConfig): void {
  const root = document.documentElement;
  const { colors } = theme;

  // Set CSS variables
  root.style.setProperty("--bg", colors.bg);
  root.style.setProperty("--surface", colors.surface);
  root.style.setProperty("--surface2", colors.surface2);
  root.style.setProperty("--text", colors.text);
  root.style.setProperty("--muted", colors.muted);
  root.style.setProperty("--text-tertiary", colors.textTertiary);
  root.style.setProperty("--text-muted", colors.textMuted);
  root.style.setProperty("--border", colors.border);
  root.style.setProperty("--accent", colors.accent);
  root.style.setProperty("--accentText", colors.accentText);
  root.style.setProperty("--forest-green", colors.forestGreen);
  root.style.setProperty("--forest-green-light", colors.forestGreenLight);
  root.style.setProperty("--forest-green-glow", colors.forestGreenGlow);
  root.style.setProperty("--terracotta", colors.terracotta);
  root.style.setProperty("--terracotta-glow", colors.terracottaGlow);
  root.style.setProperty("--danger", colors.danger);
  root.style.setProperty("--dangerBg", colors.dangerBg);
  root.style.setProperty("--dangerText", colors.dangerText);
  root.style.setProperty("--success-bg", colors.successBg);
  root.style.setProperty("--success-text", colors.successText);
  root.style.setProperty("--warning-bg", colors.warningBg);
  root.style.setProperty("--warning-text", colors.warningText);
  root.style.setProperty("--info-bg", colors.infoBg);
  root.style.setProperty("--info-text", colors.infoText);
  root.style.setProperty("--overlay", colors.overlay);
  root.style.setProperty("--shadow", colors.shadow);
  root.style.setProperty("--shadow-ring", colors.shadowRing);

  // Set color-scheme for native elements
  root.style.colorScheme = theme.category;

  // Set data attributes for CSS selectors
  root.dataset.theme = theme.id;
  root.dataset.themeCategory = theme.category;

  // Update meta theme-color so browser chrome matches the theme
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute("content", `hsl(${colors.bg})`);
  }
}

function loadStoredThemeId(): ThemeId {
  if (typeof window === "undefined") return "system";
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && isValidThemeId(stored)) {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return "system";
}

function saveThemeId(themeId: ThemeId): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
  } catch {
    // localStorage not available
  }
}

function isValidThemeId(value: string): value is ThemeId {
  return ["paper", "midnight", "forest", "sunset", "ocean", "pro", "rose", "lavender", "mint", "sand", "playful", "system"].includes(value);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeId, setThemeIdState] = useState<ThemeId>(() => loadStoredThemeId());
  const [systemDark, setSystemDark] = useState<boolean>(() => getSystemPrefersDark());

  // Resolve the actual theme based on themeId and system preference
  const theme = useMemo(() => {
    if (themeId === "system") {
      return systemDark ? systemDarkDefault : systemLightDefault;
    }
    return themes[themeId] ?? themes.paper;
  }, [themeId, systemDark]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeIdState(id);
    saveThemeId(id);
  }, []);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Listen for OS preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemDark(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Listen for storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue && isValidThemeId(e.newValue)) {
        setThemeIdState(e.newValue);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const value: ThemeContextValue = useMemo(
    () => ({
      themeId,
      theme,
      isDark: theme.category === "dark",
      needsButtonRings: theme.needsButtonRings,
      needsGlassEffect: theme.needsGlassEffect,
      setTheme,
    }),
    [themeId, theme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
